import { useEffect, useState } from 'react';
import { Mail, X, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Stores a 'dismissed' / 'subscribed' flag so the popup only shows once.
// Non-sensitive UI state — localStorage is correct. See /app/CODE_REVIEW_NOTES.md §4.
const STORAGE_KEY = 'ww-email-popup';
const DELAY_MS = 12000;

export default function EmailPopup() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Don't pester the admin (or the print/keepsake pages) — they're not shoppers.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) return;
    const t = setTimeout(() => setShow(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'dismissed');
    setShow(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/subscribe`, { email, source: 'popup' });
      localStorage.setItem(STORAGE_KEY, 'subscribed');
      setSuccess(r.data.code || 'WELCOME10');
      toast.success("You're in! Use code WELCOME10 at checkout.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in" data-testid="email-popup">
      <div className="relative bg-[#FAF9F6] max-w-md w-full rounded-sm shadow-float overflow-hidden">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 p-1.5 hover:bg-[#F5F5F0] rounded-full transition-colors z-10"
          data-testid="email-popup-close"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-[#52796F]" strokeWidth={1.5} />
        </button>

        <div className="p-8 sm:p-10 text-center">
          {success ? (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#D4AF37]/15 rounded-full mb-5">
                <Sparkles className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl text-[#354F52] mb-3">
                Welcome to the whisper
              </h3>
              <p className="text-[#52796F] mb-6">
                Your 10% code is ready. Use it at checkout.
              </p>
              <div className="bg-[#354F52] text-[#FAF9F6] py-3 px-5 rounded-sm font-mono text-lg tracking-[0.3em] mb-6" data-testid="email-popup-code">
                {success}
              </div>
              <Button onClick={dismiss} className="btn-primary w-full" data-testid="email-popup-done">
                Start Shopping
              </Button>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#D4AF37]/15 rounded-full mb-5">
                <Mail className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <p className="text-caption text-[#84A98C] mb-3">A little gift</p>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#354F52] mb-3 leading-tight">
                10% off your first bracelet
              </h3>
              <p className="text-[#52796F] mb-6 text-sm sm:text-base">
                Join the whisper. Get a one-time code, plus first dibs on new
                bracelets and seasonal drops.
              </p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="text-center"
                  data-testid="email-popup-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                  data-testid="email-popup-submit"
                >
                  {loading ? 'Sending...' : 'Send me my 10% code'}
                </Button>
              </form>
              <button
                onClick={dismiss}
                className="text-xs text-[#84A98C] mt-4 hover:text-[#354F52] transition-colors"
                data-testid="email-popup-no-thanks"
              >
                No thanks, I'll pay full price
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
